import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import { LoginDto, Shop } from '../../types';
import { shopsService } from '../../services/api';
import { AnimatedText, Stagger, StaggerItem } from '../../motion';
import { staggerItemTight } from '../../motion/variants';
import { accent, grey, stagger } from '../../theme/tokens';

const HIGHLIGHTS = [
  { icon: <ScheduleOutlinedIcon />, text: 'See every booking for your shop at a glance' },
  { icon: <NotificationsActiveOutlinedIcon />, text: 'Live updates the moment a job changes' },
  { icon: <VerifiedUserOutlinedIcon />, text: 'Your shop data stays yours alone' },
];

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginDto>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const { login } = useAuth();
  const { selectedShop } = useShop();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  // Set shop ID from context if available
  useEffect(() => {
    if (selectedShop) {
      setFormData(prev => ({
        ...prev,
        shopId: selectedShop.id
      }));
    }
  }, [selectedShop]);

  // Load shops for the dropdown (only if no shop in context)
  useEffect(() => {
    if (selectedShop) return; // Skip loading if we have a shop from context

    const fetchShops = async () => {
      try {
        setIsLoadingShops(true);
        const shopsData = await shopsService.getAllShops();
        setShops(shopsData);
      } catch (error) {
        console.error('Failed to load shops:', error);
      } finally {
        setIsLoadingShops(false);
      }
    };

    fetchShops();
  }, [selectedShop]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleShopChange = (e: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, shopId: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(formData);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const bookingLink = selectedShop
    ? `/shop/${selectedShop.name.toLowerCase().replace(/\s+/g, '-')}/book-appointment`
    : '/book-appointment';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
        minHeight: { md: 'calc(100vh - 68px)' },
      }}
    >
      {/* Brand panel — hidden on mobile so the form stays above the fold */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          px: { md: 7, lg: 10 },
          py: 8,
          color: grey[50],
          background: `linear-gradient(150deg, ${grey[950]} 0%, ${accent[900]} 65%, ${accent[800]} 100%)`,
        }}
      >
        {/* Soft light bloom behind the copy */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-15%',
            width: 520,
            height: 520,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(accent[400], 0.4)} 0%, transparent 65%)`,
            filter: 'blur(20px)',
          }}
        />

        <Box sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 5 }}>
            <BuildCircleIcon sx={{ fontSize: 34, color: accent[300] }} />
            <Typography variant="h6" fontWeight={700} letterSpacing="-0.02em">
              MechaniX
            </Typography>
          </Stack>

          <AnimatedText
            variant="h2"
            component="h1"
            text="Run your shop, not your spreadsheet."
            sx={{ maxWidth: '13ch', mb: 3 }}
          />

          <Typography variant="body1" sx={{ color: alpha(grey[50], 0.72), maxWidth: '42ch', mb: 5 }}>
            Bookings, mechanics, and invoices in one place — updated live as your day changes.
          </Typography>

          <Stagger interval={stagger.loose} delay={0.4}>
            <Stack spacing={2}>
              {HIGHLIGHTS.map((item) => (
                <StaggerItem key={item.text} variants={staggerItemTight}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        display: 'grid',
                        placeItems: 'center',
                        backgroundColor: alpha(grey[50], 0.1),
                        color: accent[200],
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography variant="body2" sx={{ color: alpha(grey[50], 0.82) }}>
                      {item.text}
                    </Typography>
                  </Stack>
                </StaggerItem>
              ))}
            </Stack>
          </Stagger>
        </Box>
      </Box>

      {/* Form panel */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xs" disableGutters>
          <Stagger interval={stagger.tight}>
            <StaggerItem variants={staggerItemTight}>
              <Typography variant="h4" component="h2" gutterBottom>
                Sign in
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Welcome back. Enter your details to continue.
              </Typography>
            </StaggerItem>

            {selectedShop && (
              <StaggerItem variants={staggerItemTight}>
                <Chip
                  icon={<StorefrontIcon />}
                  label={selectedShop.name}
                  variant="outlined"
                  sx={{ mb: 3 }}
                />
              </StaggerItem>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 2.5 }}>
                  {error}
                </Alert>
              )}

              <StaggerItem variants={staggerItemTight}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleChange}
                />
              </StaggerItem>

              <StaggerItem variants={staggerItemTight}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </StaggerItem>

              {/* Only needed when no shop came from the URL/context */}
              {!selectedShop && shops.length > 0 && (
                <StaggerItem variants={staggerItemTight}>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="shop-select-label">Shop</InputLabel>
                    <Select
                      labelId="shop-select-label"
                      id="shop-select"
                      value={formData.shopId || ''}
                      label="Shop"
                      onChange={handleShopChange}
                      disabled={isLoadingShops}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a shop</em>
                      </MenuItem>
                      {shops.map((shop) => (
                        <MenuItem key={shop.id} value={shop.id}>
                          {shop.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </StaggerItem>
              )}

              <StaggerItem variants={staggerItemTight}>
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  sx={{ mt: 3.5, mb: 2 }}
                  disabled={isLoading || (!selectedShop && !formData.shopId)}
                >
                  {isLoading ? 'Signing in…' : 'Sign in'}
                </Button>
              </StaggerItem>

              <Typography variant="body2" textAlign="center" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/forgot-password" fontWeight={600}>
                  Forgot your password?
                </Link>
              </Typography>

              <Divider sx={{ my: 2.5 }}>
                <Typography variant="caption" color="text.secondary">
                  or
                </Typography>
              </Divider>

              <Typography variant="body2" color="text.secondary" textAlign="center">
                Need a repair?{' '}
                <Link component={RouterLink} to={bookingLink} fontWeight={600}>
                  Book an appointment
                </Link>
              </Typography>
            </Box>
          </Stagger>
        </Container>
      </Box>
    </Box>
  );
};

export default LoginPage;
